/**
 * Import React libraries.
 */
import * as React from 'react';

/**
 * Import custom components.
 */
import { MoreVertical, ChevronRight, ChevronLeft, ArrowLineDown, ArrowLineUp } from '../icons/index';
import { DropdownFilter } from '../Dropdown/index';
import { UIIconButton, Button } from '../Button/index';

/**
 * Import custom styles.
 */
import { COLORS } from '../styles';
import { TableHeaderRowCol, TableColumnHeaderCol, TableContainer, TableDataCol, TableDataRow, TableFooterCol } from './TableStyles';

/**
 * Import custom types.
 */
import { TableProps, TableColumn, TableSortingDirection } from './TableTypes';

const Table: React.FunctionComponent<TableProps> = ({ rowClick, linkClick, previousPageClick, nextPageClick, rowsPerPageChange, triggerSortingChange, title, columns, rows, remotePagination, pagination }: TableProps) => {
  // States used within the component
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [filteredRows, setFilteredRows] = React.useState<any[]>([]);
  const [currentPageRows, setCurrentPageRows] = React.useState<any[]>([]);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [sortedColumn, setSortedColumn] = React.useState<string>('');
  const [sortedDirection, setSortedDirection] = React.useState<TableSortingDirection>('asc');
  const [from, setFrom] = React.useState<number>();
  const [to, setTo] = React.useState<number>();
  const [total, setTotal] = React.useState<number>();

  /**
   * Takes care of local pagination.
   * Works only in case remote pagination is not defined or is set to false.
   */
  React.useEffect(() => {
    if (!remotePagination) {
      // Let's retrieve temporary filtered rows and update globally filtered rows
      const tmpFilteredRows = filterAndSortRows();
      setFilteredRows(tmpFilteredRows);
      setTotal(tmpFilteredRows.length);
    
      // Assign rows from and rows to
      const rowsFrom = (currentPage - 1) * rowsPerPage;
      const rowsTo = rowsFrom + rowsPerPage >= tmpFilteredRows.length ? tmpFilteredRows.length : rowsFrom + rowsPerPage;
  
      // Set from and to values
      setFrom(rowsFrom + 1);
      setTo(rowsTo);
  
      // Filter out the rows
      setCurrentPageRows(tmpFilteredRows.slice(rowsFrom, rowsTo));
    }
  }, [rowsPerPage, rows, currentPage, columns, sortedColumn, sortedDirection, remotePagination]);

  /**
   * Takes care of remote pagination.
   * Works only in case remote pagination is set to true.
   */
  React.useEffect(() => {
    if (remotePagination) {
      setCurrentPageRows(rows);

      // Let's assign pagination values
      setFrom(pagination?.from);
      setTo(pagination?.to);
      setTotal(pagination?.total);
      setCurrentPage(pagination?.currentPage!);
    }
  }, [remotePagination, pagination, rows]);

  /**
   * Filters out a rows by specific column filters and sorts them if any sorting is set.
   * @returns Filtered and sorted rows.
   */
  const filterAndSortRows = (): any[] => {
    let tmpFilteredRows = rows.filter(row => {
      // Let's start with matched filter criteria
      let matchFilterCriteria = true;

      // Let's go through columns
      for (const column of columns) {
        // Let's check if filter value is specified for the column
        // And if it doesn't match the current row column value
        // Then let's update match filter criteria to failed
        if (column.filterValue && row[column.key].toLowerCase().indexOf(column.filterValue.toLowerCase()) === -1) {
          matchFilterCriteria = false;
        }
      }

      // Let's return row only in case match filter criteria succeeds
      if (matchFilterCriteria) {
        return row;
      }
    });

    // Let's apply sorting if needed
    if (columns.some(column => column.sortingDirection)) {
      // Let's find the sorting column
      const sortingColumn = columns.find(column => column.sortingDirection)!;

      // Update sorted column data
      setSortedDirection(sortingColumn.sortingDirection!);
      setSortedColumn(sortingColumn.key!);

      // Let's sort the rows
      tmpFilteredRows.sort((a, b) => {
        if (a[sortingColumn.key] > b[sortingColumn.key]) {
          return sortingColumn.sortingDirection === 'asc' ? 1 : -1;
        } else if (a[sortingColumn.key] < b[sortingColumn.key]) {
          return sortingColumn.sortingDirection === 'asc' ? -1 : 1;
        } else {
          return 0;
        }
      });
    }

    // Let's return filtered rows
    return tmpFilteredRows;
  }

  /**
   * Applies a specific sorting to a column.
   * If no sorting exists, then applies ascending initially
   * @param column - Column to which sorting should be applied.
   */
  const sortTableColumn = (column: TableColumn): void => {
    // Let's remove any active sorting
    columns = columns.map((tmpColumn: TableColumn) => {
      // Let's apply new sorting
      // Let's check if it is the column for which we have to apply sorting
      if (column.key === tmpColumn.key) {
        // Let's check if sorting is not defined
        // If so, let's toggle the sorting direction
        if (tmpColumn.sortingDirection !== undefined) {
          if (tmpColumn.sortingDirection === 'asc') {
            tmpColumn.sortingDirection = 'desc';

            // Update sorted column and direction
            setSortedColumn(column.key);
            setSortedDirection('desc');

            // Let's trigger remote sorting trigger function
            if (remotePagination && triggerSortingChange) {
              triggerSortingChange(column.key, 'desc');
            }
          } else {
            tmpColumn.sortingDirection = 'asc';

            // Update sorted column and direction
            setSortedColumn(column.key);
            setSortedDirection('asc');

            // Let's trigger remote sorting trigger function
            if (remotePagination && triggerSortingChange) {
              triggerSortingChange(column.key, 'asc');
            }
          }
        } else {
          // Let's apply the initial sorting
          tmpColumn.sortingDirection = 'asc';

          // Update sorted column and direction
          setSortedColumn(column.key);
          setSortedDirection('asc');

          // Let's trigger remote sorting trigger function
          if (remotePagination && triggerSortingChange) {
            triggerSortingChange(column.key, 'asc');
          }
        }
      } else if (tmpColumn.sortingDirection !== undefined) {
        // Let's remove sorting
        tmpColumn.sortingDirection = undefined;
      }

      return tmpColumn;
    });
  };

  /**
   * Navigates user back to the previous page and updates the current table page.
   */
  const previousPage = () => {
    if (remotePagination) {
      // Let's inform parent component about page change
      if (previousPageClick) {
        previousPageClick();
      }
    } else {
      // Let's change the page within the component
      setCurrentPage(currentPage > 1 ? currentPage - 1 : 1);
    }
  }

  /**
   * Navigates user to the next page and updates the current table page.
   */
  const nextPage = () => {
    if (remotePagination) {
      // Let's inform parent component about page change
      if (nextPageClick) {
        nextPageClick();
      }
    } else {
      // Let's change the page within the component
      setCurrentPage(currentPage * rowsPerPage >= filteredRows.length ? currentPage : currentPage + 1);
    }
  }

  /**
   * Updates the rows per page value and refreshes the table content accordingly.
   * @param value - Newly selected rows per page value.
   */
  const changeRowsPerPage = (value: string) => {
    // Let's check if we are using remote pagination
    if (remotePagination) {
      // Inform parent component about rows per page change
      if (rowsPerPageChange) {
        rowsPerPageChange(parseInt(value));
      }
    } else {
      // Let's reset current page to 1
      setCurrentPage(1);
    }

    // Let's set rows per page
    setRowsPerPage(parseInt(value));
  }

  /**
   * Renders the table header.
   */
  const renderHeader = () => {
    return (
      <thead>
        { title && 
          <tr data-testid="TestTableHeaderRow">
            <TableHeaderRowCol colSpan={columns.length}>
              <div>
                { title }
                <MoreVertical size="18" color={COLORS.neutral_600} />  
              </div>
            </TableHeaderRowCol>
          </tr>
        }
        <tr data-testid="TestTableColumnHeaderRow">
          {
            columns.map((column: TableColumn) => (
              <TableColumnHeaderCol onClick={() => sortTableColumn(column)} key={column.key} className={`${column.sortable ? 'sortable' : ''} ${!title ? 'no-border' : ''}`}>
                { column.name }

                {
                  column.sortingDirection === 'asc'
                    ? <ArrowLineUp size="20" />
                    : column.sortingDirection === 'desc'
                    ? <ArrowLineDown size="20" />
                    : ''
                }
              </TableColumnHeaderCol>
            ))
          }
        </tr>
      </thead>
    );
  }

  /**
   * Function which is called when a mouse click happens on a link to pass data to the parent component.
   * @param row - Row in which the link is located.
   * @param key - Key of the column for which the link is set.
   * @param event - Click event handler.
   */
  const onLinkClick = (row: any, key: string, event: React.MouseEvent) => {
    if (linkClick) {
      event.stopPropagation();

      linkClick(row, key);
    }
  };

  /**
   * Function which is called when a mouse click happens on a row to pass data to the parent component.
   * @param row - Row in which the link is located.
   */
  const onRowClick = (row: any): void => {
    const selectedText = window?.getSelection()?.toString();

    // Let's trigger row click only if we haven't selected any text and action exists
    if (selectedText?.length === 0 && rowClick) {
      rowClick(row);
    }
  };

  /**
   * Renders the table body.
   */
  const renderBody = () => {
    return (
      <tbody>
        {
          currentPageRows.length > 0 ?
            currentPageRows.map((row: any, index: number) => (
              <TableDataRow key={`row_${index}`} onClick={() => onRowClick(row)} data-testid="TestTableDataRow">
                {
                  columns.map((column: TableColumn) => (
                    <TableDataCol key={`row_${index}_${column.key}`}>
                      {
                        column.isLink && row[column.key]
                        ? <Button size='small' variant='text' onClick={(event) => onLinkClick(row, column.key, event) }>{ row[column.key] }</Button>
                        : row[column.key]
                      }
                    </TableDataCol>
                  ))
                }
              </TableDataRow>
            ))
          :
            <TableDataRow className="no-rows" data-testid="TestTableNoDataRow">
              <TableDataCol colSpan={columns.length} className="center">
                There are no rows to display
              </TableDataCol>
            </TableDataRow>
        }
      </tbody>
    );
  }

  /**
   * Renders the table footer.
   */
  const renderFooter = () => {
    // Define local values used within the footer
    const rowsPerPageValues = ['10', '20', '50', '100'];

    return (
      <tfoot>
        <tr>
          <TableFooterCol colSpan={columns.length} data-testid="TestTableFooterRow">
            <div>
              <span>Rows per page: </span>
              <DropdownFilter id="rows-per-page" list={rowsPerPageValues} disableSorting={true} onSelect={value => changeRowsPerPage(value)} initalValue="10" isButton={true} />
              <span className="current-page-info">{ from }-{ to } of { total }</span>
              <div className="controls">
                <UIIconButton action={() => previousPage()} disabled={from === 1}>
                  <ChevronLeft size="24" color={COLORS.neutral_600} />
                </UIIconButton>
                <UIIconButton action={() => nextPage()} disabled={to === total}>
                  <ChevronRight size="24" color={COLORS.neutral_600} />
                </UIIconButton> 
              </div>
            </div>
          </TableFooterCol>
        </tr>
      </tfoot>
    );
  }

  /**
   * Return Table component.
   */
  return (
    <>
      <TableContainer cellPadding="0" cellSpacing="0" data-testid="TestTable">
        { renderHeader() }
        { renderBody() }
        { renderFooter() }
      </TableContainer>
    </>
  );
};

export default Table;
